export const NOVA_OBRA_TITLE = "Nova Obra";
export const ETAPA_OBRA_TITLE = "Dados da Obra";
export const ETAPA_OBRA_SUBTITLE = "Preencha as informações sobre a obra da embarcação.";
export const REQUIRED_FIELDS_NOTE = "*Campos obrigatórios";

export const SECTION_IDENTIFICACAO = "Identificação";
export const SECTION_STATUS_CONDICOES = "Status e Condições";
export const SECTION_DETALHES = "Detalhes";

export const FIELD_LABELS = {
  embarcacao: "Embarcação *",
  localPlanejado: "Local Planejado",
  manutencaoCorretiva: "Manutenção Corretiva",
  statusObra: "Status da Obra *",
  statusFinanceiro: "Status Financeiro *",
  observacoes: "Observações",
  descricao: "Breve Descrição da Obra *",
} as const;

export const SELECT_PLACEHOLDER = "Selecione";

export const STATUS_FINANCEIRO_TOOLTIP =
  "Toda obra começa em FEL-0. Para mudar, ative Manutenção Corretiva.";
export const DESCRICAO_TOOLTIP = "Descreva brevemente o objetivo e a justificativa da obra.";

export const INFO_EMBARCACAO = {
  title: "Informações da Embarcação",
  empty: "Selecione uma embarcação para visualizar os dados.",
  loading: "Buscando dados da embarcação...",
  error: "Não foi possível carregar os dados da embarcação.",
  clienteAtual: "Cliente Atual",
  terminoContratoCampo: "Término de Contrato",
  tipoEmbarcacao: "Tipo de Embarcação",
  ultimaObra: "Última Obra",
  semUltimaObra: "Nenhuma obra registrada",
  terminoContrato: "Término de contrato",
  previsaoExtensao: "Previsão de extensão contratual",
  limitesClasse: "Limites de classe",
  verTodos: "Ver todos",
  tooltipDuranteObra: "Vence durante a obra",
} as const;

/** Quantos limites de classe aparecem como tag antes do "+N" (card da etapa 1). */
export const LIMITES_CLASSE_VISIVEIS = 2;
/** Card de limites das etapas 2 e 3 (Figma `10446:107810`): 3 tags + chip "+N". */
export const LIMITES_CLASSE_VISIVEIS_ETAPAS = 3;

export const BUTTONS = {
  cancelar: "Cancelar",
  voltar: "Voltar",
  salvarRascunho: "Salvar Rascunho",
  proximaEtapa: "Próxima etapa",
  criarObra: "Criar Obra",
} as const;

export const TROCAR_EMBARCACAO_DIALOG = {
  title: "Trocar embarcação",
  text: "Ao trocar a embarcação, serão limpos os equipamentos selecionados dos grupos Motor, Thruster e Propulsão e removidas as seções de centros de custo adicionais da embarcação anterior, com seus tipos de obra e escopos. Deseja continuar?",
  confirm: "Trocar embarcação",
  cancel: "Manter embarcação",
} as const;

export const CANCELAR_DIALOG = {
  title: "Cancelar Nova Obra",
  text: "Há alterações não salvas. Se sair agora, os dados preenchidos serão descartados. Deseja sair?",
  confirm: "Sair e descartar",
  cancel: "Continuar editando",
} as const;

export const ETAPA_DURACAO_TITLE = "Duração";
export const ETAPA_DURACAO_SUBTITLE =
  "Informe a data de início e adicione os períodos em ordem, um após o outro.";

export const DURACAO_LABELS = {
  inicioObra: "Data de início",
  /** Rótulo do Início no resumo (Figma `10446:107810`: "Início", sem "da Obra"). */
  inicioResumo: "Início",
  terminoPrevisto: "Término previsto",
  duracaoTotal: "Duração total",
  periodosTitle: "Períodos da obra",
  adicionarPeriodo: "Adicionar Período",
  colPeriodo: "Período *",
  colInicio: "Início",
  colDuracao: "Duração (d)",
  colFim: "Fim",
  colMeta: "Meta (dias)",
  removerPeriodo: "Remover período",
  obra: "Obra",
} as const;

/** HU 2523 RN04: período de classificação Obra se identifica como "Obra – [nome do período]". */
export const rotuloPeriodo = (descricao: string, etapa?: string | null) =>
  etapa === "OBRA" ? `${DURACAO_LABELS.obra} – ${descricao}` : descricao;

export const DURACAO_PLACEHOLDERS = {
  data: "DD/MM/AAAA",
  duracao: "Dias",
  fim: "Calculado",
} as const;

export const DURACAO_DURACAO_TOOLTIP = "Informar duração em dias corridos.";

export const DURACAO_EMPTY = {
  semInicio: "Informe a data de início para adicionar o primeiro período.",
  comInicio: "Clique em adicionar período para começar a inserir as datas.",
} as const;

export const DURACAO_SEM_PERIODO_OBRA_MESSAGE =
  "Adicione o período Obra para seguir. O início da obra define a equipe, os escopos macro e o limite de classe.";

export const DURACAO_TOAST = {
  summary: "Período removido",
  detail: (nome: string) => `${nome} saiu da lista. `,
  desfazer: "Desfazer",
  semNome: "O período",
} as const;

export const EQUIPE_ETAPA_TITULO = "Equipe de Obra";
export const EQUIPE_ETAPA_APOIO = "Defina a equipe que vai realizar a obra na embarcação.";
export const EQUIPE_ETAPA_SEM_PERIODO =
  "Defina o início e os períodos da obra na etapa Duração para montar a equipe.";

export const ETAPA_TIPOS_TITLE = "Tipos de Obra";
export const ETAPA_TIPOS_SUBTITLE = "Adicione os tipos de obra e os escopos macro de cada um.";

/** Texto de campo cujo dado ainda carrega (Etapas 2 e 3), no lugar de "Selecione"/"—". */
export const CARREGANDO = "Carregando…";

export const TIPOS_LABELS = {
  embarcacao: "Embarcação",
  unidadeNegocio: "Unidade de Negócio",
  empresa: "Empresa",
  periodoObra: "Período da obra",
  centroCusto: "Centro de custo",
  centroCustoObrigatorio: "Centro de custo *",
  centroCustoHint: "Vem com o centro de custo de obra (X) da embarcação.",
  centroCustoPlaceholder: "Selecione o centro de custo",
  centroCustoSemOpcoes: "Nenhuma opção disponível.",
  centroCustoSemCadastro: "Sem centro de custo cadastrado",
  centroCustoSemCadastroHint:
    "A embarcação não tem centro de custo cadastrado. O escopo é montado sem centro de custo principal.",
  tiposTitle: "Tipos de obra",
  adicionarTipo: "Adicionar tipo de obra",
  outrosCentrosTitle: "Outros centros de custo (opcional)",
  outrosCentrosText:
    "Use quando parte da obra deve ser apurada fora do centro de custo principal, como na manutenção operacional (Y) ou em um projeto SubSea.",
  adicionarCentro: "Adicionar centro de custo",
  buscarCentro: "Buscar centro de custo",
  semResultadoCentro: "Nenhum centro de custo encontrado.",
  todosCentrosAdicionados: "Todos os centros de custo desta embarcação já foram adicionados.",
  grupoManutencaoOperacional: "Manutenção Operacional",
  grupoSubsea: "SubSea",
  // Figma `10465:701181` ("Cliente"/"Contrato") sem o asterisco: a HU 2525 diz que os dois campos não são
  // obrigatórios no planejamento inicial (decisão do usuário), então não há asterisco nem validação.
  cliente: "Cliente",
  clientePlaceholder: "Selecione",
  contrato: "Contrato",
  contratoPlaceholder: "Número ou nome do contrato",
  inspecaoCasco: "Haverá inspeção de casco?",
  inspecaoCascoPlaceholder: "Selecione o Tipo",
  semEscopo: "Sem Escopo",
  semTipoDoCentro: "Nenhum tipo de obra adicionado.",
  semTipoDoCentroAceita: (codigo: string, nome: string) =>
    `Este centro de custo aceita só o tipo ${codigo} (${nome}).`,
} as const;

export const TIPOS_VAZIO = "Adicione um tipo de obra para começar.";

export const ESCOPO_VAZIO = {
  title: "Nenhum Escopo Macro adicionado.",
  text: "Você pode seguir com a criação da obra, mas será gerada uma pendência para completar depois.",
  adicionar: "Adicionar escopo macro",
} as const;

/** Casca visual do Escopo Macro (Figma `tipo-obra/card` 10691:1406); o fluxo é do épico Escopo Macro. */
export const ESCOPO_MACRO = {
  titulo: "Escopos Macro",
  quantidade: (total: number) => `${total} ${total === 1 ? "Escopo" : "Escopos"}`,
  novoTitulo: "Novo Escopo Macro",
  incompleto: "Faltam campos",
  adicionar: "Adicionar escopo macro",
  expandir: "Expandir escopo macro",
  recolher: "Recolher escopo macro",
  remover: "Remover escopo macro",
  campos: {
    escopo: { label: "Escopo macro *", placeholder: "Descreva o escopo macro" },
    inicio: { label: "Início previsto *", placeholder: "01/06/2026" },
    fim: { label: "Fim previsto *", placeholder: "DD/MM/AAAA" },
    origem: { label: "Origem *", placeholder: "Selecione" },
    descricao: { label: "Descrição", placeholder: "Descrição (opcional)" },
  },
} as const;

export const TIPOS_A11Y = {
  expandir: "Expandir",
  recolher: "Recolher",
  removerTipo: "Remover tipo de obra",
  removerTipoAutomatico:
    "Este tipo vem da Manutenção Corretiva (etapa 1). Desative-a para removê-lo.",
  removerCentro: "Remover centro de custo",
  /** Tooltip da lixeira do tipo (Figma `12407:329681`). */
  tooltipRemoverTipo: "Remover tipo de obra e escopos",
} as const;

export const TIPOS_TOAST = {
  tipoSummary: "Tipo de obra removido",
  naoRestauradoSummary: "Não foi possível desfazer",
  naoRestaurado: (nome: string, motivo: "repetido" | "excludente" | "nao-aceito") =>
    motivo === "repetido"
      ? `${nome} já foi adicionado a este centro de custo e não pode ser repetido.`
      : motivo === "excludente"
        ? `${nome} não pode voltar: o tipo excludente (101 × 102) já está na obra.`
        : `${nome} não é mais aceito neste centro de custo.`,
  centroSemCorretivaSummary: "Centro de custo não permitido",
  centroSemCorretiva: (centro: string) =>
    `O centro de custo ${centro} não aceita o tipo 301 · Manutenção Corretiva, exigido pela Manutenção Corretiva marcada na obra.`,
  centroSemTiposRestaurados: (nomes: string) =>
    `Estes tipos não voltaram, pois conflitam com a obra: ${nomes}.`,
  tipoDetail: (nome: string) => `${nome} saiu da obra, com os escopos e dados dele. `,
  centroSummary: "Centro de custo removido",
  centroDetail: (nome: string) => `${nome} saiu da obra, com os tipos e escopos dele. `,
  desfazer: "Desfazer",
} as const;

export const DOMINANTE_LABELS = {
  selo: "Obra dominante",
  opcao: "Tornar esse tipo de obra como dominante?",
  info: "Prioridade do tipo de obra dominante",
  tooltipIntro:
    "O tipo de obra dominante é definido automaticamente pela prioridade abaixo. Ative esta opção para personalizar.",
} as const;

export const DOMINANTE_DIALOG = {
  title: "Alterar tipo de obra dominante",
  subtitle: "Essa ação altera o tipo de obra dominante padrão da obra.",
  atual: "Atual",
  novo: "Novo dominante",
  aviso: "Essa alteração será atualizada para refletir o novo tipo de obra dominante.",
  justificativa: "Justificativa",
  obrigatorio: "(Campo obrigatório)",
  voltar: "Voltar",
  confirmar: "Confirmar Alteração",
  fechar: "Fechar",
} as const;

/** HU 2549: modal de consulta "Ver todos" dos limites de classe (Figma `10229:689104`). */
export const LIMITES_DIALOG = {
  title: "Limites de classe",
  fechar: "Fechar",
  vistoria: "Vistoria",
  dataLimite: "Data limite",
  vencimento: "Vencimento",
  emRelacaoObra: "Em relação à obra",
  vazio: "Nenhum limite de classe cadastrado para esta embarcação.",
  carregando: "Carregando limites de classe…",
  nota: "Ordenado pela data limite. Prazo contado a partir de hoje",
  fonte: "Fonte: aba Classe da embarcação.",
} as const;

/** HU 2549: modal "Justificar e criar obra" (Figma `11202:416154`). */
export const JUSTIFICATIVA_DIALOG = {
  subtitle: "Confira antes de criar a obra.",
  fechar: "Fechar",
  periodoObra: "Período da obra",
  limiteClasse: "Limite de classe",
  terminoContrato: "Término de contrato",
  vistoria: "Vistoria",
  dataLimite: "Data limite",
  emRelacaoObra: "Em relação à obra",
  renovaCom: "Renova com",
  semTipo: "Sem tipo de obra associado",
  justificativa: "Justificativa",
  obrigatorio: "*",
  ajustarDatas: "Ajustar datas",
  adicionarTipo: "Adicionar tipo de obra",
  justificarCriar: "Justificar e criar obra",
  dias: (total: number) => `${total} ${total === 1 ? "dia" : "dias"}`,
} as const;

/** HU 2543: Etapa 5 "Revisão" (Figma `11209:107`). */
export const REVISAO = {
  title: "Revisão",
  subtitle: "Confira os dados antes de criar a obra.",
  /** MS001. */
  avisoId: "O ID da obra é gerado automaticamente depois da criação.",
  editar: "Editar",
  /** Comentário de 10/10: "Cliente atual/futuro" vira "Cliente deste Contrato". */
  clienteContrato: "Cliente deste Contrato",
  vazio: "—",
  secoes: {
    dadosObra: "Dados da Obra",
    duracao: "Duração",
    tiposObra: "Tipos de Obra",
    equipe: "Equipe",
  },
  dados: {
    identificacao: "Identificação",
    statusCondicoes: "Status e Condições",
    detalhes: "Detalhes",
    embarcacao: "Embarcação",
    localPlanejado: "Local planejado",
    localAdefinir: "A definir",
    manutencaoCorretiva: "Manutenção corretiva",
    sim: "Sim",
    nao: "Não",
    statusObra: "Status da obra",
    statusFinanceiro: "Status financeiro",
    observacoes: "Observações",
    descricao: "Breve descrição da obra",
  },
  duracao: {
    datasObra: "Datas da obra",
    inicioObra: "Início da obra",
    terminoPrevisto: "Término previsto",
    duracaoTotal: "Duração total",
    /** Tooltip do campo Duração total (Figma `12401:175069`). */
    duracaoTotalTooltip: "Dias corridos do início da obra ao fim do último período.",
    linhaDoTempo: "Linha do tempo",
    tabela: "Tabela",
    gantt: "Gantt",
    alternadorLabel: "Visão da linha do tempo",
    colPeriodo: "Período",
    colInicio: "Data início",
    colFim: "Data fim",
    colDuracao: "Duração (d)",
    colMeta: "Meta (d)",
    semPeriodos: "Nenhum período informado.",
    /** Gantt da Revisão (Figma `gantt-parada` 10706:406743). */
    ganttLabel: "Gantt da linha do tempo",
    legenda: "Legenda:",
    legendaNavegacao: "Navegação",
    legendaPermanencia: "Fundeio, atracação e dique",
    legendaObra: "Período de obra",
    legendaComissionamento: "Testes e aceitação",
    legendaTotal: "Total da parada",
    legendaInicio: "Início",
    legendaLimite: "Limite de classe",
    ganttTotal: "Total da parada",
    ganttObra: "Período de obra",
    ganttLimite: (nome: string) => `Limite · ${nome}`,
    /** Tooltip da barra (Figma `tooltip-gantt-periodo` 11110:56949). */
    tipPlanejado: "planejado",
    tipPeriodo: "Período",
    tipMeta: "Meta",
    dias: (total: number) => `${total} ${total === 1 ? "dia" : "dias"}`,
  },
  tipos: {
    centroCusto: "Centro de custo",
    unidadeNegocio: "Unidade de Negócio",
    empresa: "Empresa",
    tipos: (total: number) => `TIPOS DE OBRA · ${total}`,
    outrosCentros: (total: number) => `OUTROS CENTROS DE CUSTO · ${total}`,
    semTipos: "Nenhum tipo de obra adicionado.",
    semCentro: "Sem centro de custo cadastrado",
    cliente: "Cliente deste Contrato",
    contrato: "Contrato",
    inspecaoCasco: "Inspeção de casco",
    escoposMacro: "Escopos Macro",
    /** Figma `10728:358`: sem escopo o contador diz "Sem Escopo", nunca "0 Escopos". */
    escopos: (total: number) =>
      total === 0 ? "Sem Escopo" : `${total} ${total === 1 ? "Escopo" : "Escopos"}`,
    inicioPrevisto: "Início previsto",
    fimPrevisto: "Fim previsto",
    origem: "Origem",
    descricao: "Descrição",
    /** MS002. */
    semEscopo:
      "Sem escopo macro. A obra pode ser criada, mas fica uma pendência para completar depois.",
  },
  equipe: {
    colProfissional: "Nome",
    colPapel: "Papel na Obra",
    colInicio: "Data Início",
    colFim: "Data Fim",
    vazio: "Nenhum profissional na equipe.",
    /** MS004. */
    papelPendente: (papel: string, data: string, referencia: string) =>
      `Falta o papel obrigatório ${papel}, com prazo até ${data} (${referencia}). A obra pode ser criada, mas fica uma pendência até ele ser adicionado.`,
    /** MS003 (HU 2543): uma alocação conflitante usa a forma do protótipo; mais de uma, o plural. */
    conflito: (profissional: string, periodos: readonly { inicio: string; fim: string }[]) => {
      const trecho = (periodo: { inicio: string; fim: string }) =>
        `entre ${periodo.inicio} e ${periodo.fim}`;
      return periodos.length === 1
        ? `${profissional} tem 1 conflito de alocação no período da obra (outra obra ${trecho(periodos[0]!)}).`
        : `${profissional} tem ${periodos.length} conflitos de alocação no período da obra (outras obras ${periodos.map(trecho).join("; ")}).`;
    },
    papelPendenteSemPrazo: (papel: string) =>
      `Falta o papel obrigatório ${papel}. A obra pode ser criada, mas fica uma pendência até ele ser adicionado.`,
  },
  condicoes: {
    carregando: "Verificando limites de classe e contrato da embarcação…",
    erro: "Não foi possível verificar os limites de classe e o contrato da embarcação. Atualize a página para tentar novamente; a obra só pode ser criada depois dessa verificação.",
  },
} as const;

export const CRIAR_OBRA = {
  sucesso: "Obra criada com sucesso.",
  sucessoSummary: "Obra criada",
  erroSummary: "Não foi possível criar a obra",
  criando: "Criando…",
} as const;

/** Opção de select `{ value, label }`. */
export interface SelectOption<T extends string = string> {
  readonly value: T;
  readonly label: string;
}
